/* =========================================================
   SCENT LABS — CUSTOM CART DRAWER (V12 — REWARDS + GIFTS + UPSELLS)
   assets/scentlabs-cart-drawer.js
   ========================================================= */

(() => {
  'use strict';

  if (window.__scentLabsCartDrawerV7Loaded) return;
  window.__scentLabsCartDrawerV7Loaded = true;

  const DRAWER_SELECTOR = 'cart-drawer.sl-cart-drawer';
  const DRAWER_SECTION = 'scentlabs-cart-drawer';
  const BUBBLE_SECTION = 'cart-icon-bubble';

  const EXTERNAL_CART_EVENTS = [
    'cart:refresh',
    'cart:updated',
    'cart:update',
    'cart:change',
    'cart:build',
    'ajaxCart:updated',
    'theme:cart:change',
    'cart-drawer:refresh',
    'cartdrawer:refresh',
    'cartDrawer:refresh',
    'cart:open',
    'drawer:open'
  ];

  const controllers = new WeakMap();

  const getRoot = () => {
    const root = window.Shopify?.routes?.root || '/';
    return root.endsWith('/') ? root : `${root}/`;
  };

  const getSectionsContext = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('sections');
    url.searchParams.delete('section_id');
    return `${url.pathname}${url.search}`;
  };

  const getSectionsUrl = () => {
    const url = new URL(window.location.href);
    url.searchParams.delete('section_id');
    url.searchParams.set('sections', `${DRAWER_SECTION},${BUBBLE_SECTION}`);
    return url.toString();
  };

  const getSingleSectionUrl = (sectionId) => {
    const url = new URL(window.location.href);
    url.searchParams.delete('sections');
    url.searchParams.set('section_id', sectionId);
    return url.toString();
  };

  const parseHTML = (html) => {
    if (!html || typeof html !== 'string') return null;
    return new DOMParser().parseFromString(html, 'text/html');
  };

  const getFocusable = (root) => {
    if (!root) return [];

    return Array.from(
      root.querySelectorAll(
        'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])'
      )
    ).filter((element) => {
      if (element.closest('[hidden], [aria-hidden="true"]')) return false;

      const style = window.getComputedStyle(element);

      return style.display !== 'none' && style.visibility !== 'hidden';
    });
  };

  const isAbortError = (error) => error?.name === 'AbortError';

  const installFallbackDrawerAPI = (drawer) => {
    if (!drawer || typeof drawer.open === 'function') return false;

    let activeElement = null;

    const close = () => {
      drawer.classList.remove('active');
      document.body.classList.remove('overflow-hidden');
      document.removeEventListener('keydown', onDocumentKeydown);
      drawer.setAttribute('aria-hidden', 'true');

      if (activeElement instanceof HTMLElement) {
        window.setTimeout(() => {
          activeElement?.focus?.({ preventScroll: true });
        }, 50);
      }
    };

    const onDocumentKeydown = (event) => {
      if (!drawer.classList.contains('active')) return;

      if (event.key === 'Escape') {
        event.preventDefault();
        close();
        return;
      }

      if (event.key !== 'Tab') return;

      const panel = drawer.querySelector('.drawer__inner');
      const focusable = getFocusable(panel);

      if (!focusable.length) {
        event.preventDefault();
        panel?.focus({ preventScroll: true });
        return;
      }

      const first = focusable[0];
      const last = focusable[focusable.length - 1];

      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault();
        last.focus();
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault();
        first.focus();
      }
    };

    drawer.setActiveElement = (element) => {
      activeElement = element || document.activeElement;
    };

    drawer.open = (triggeredBy) => {
      if (triggeredBy) {
        drawer.setActiveElement(triggeredBy);
      }

      drawer.classList.add('animate');

      window.requestAnimationFrame(() => {
        drawer.classList.add('active');
      });

      document.body.classList.add('overflow-hidden');
      document.addEventListener('keydown', onDocumentKeydown);

      drawer.setAttribute('aria-hidden', 'false');

      window.setTimeout(() => {
        drawer
          .querySelector('.sl-cart-drawer__close')
          ?.focus({ preventScroll: true });
      }, 30);
    };

    drawer.close = close;

    drawer.dataset.slFallback = 'true';
    drawer.setAttribute('aria-hidden', 'true');

    const overlay = drawer.querySelector('#CartDrawer-Overlay');

    if (overlay && overlay.dataset.slFallbackBound !== 'true') {
      overlay.dataset.slFallbackBound = 'true';
      overlay.addEventListener('click', close);
    }

    const cartLink = document.querySelector('#cart-icon-bubble');

    const onFallbackCartKeydown = (event) => {
      if (event.code.toUpperCase() !== 'SPACE') return;

      event.preventDefault();
      drawer.open(cartLink);
    };

    if (cartLink) {
      cartLink.setAttribute('role', 'button');
      cartLink.setAttribute('aria-haspopup', 'dialog');
      cartLink.setAttribute('aria-controls', 'CartDrawer');

      if (cartLink.dataset.slFallbackKeyboardBound !== 'true') {
        cartLink.dataset.slFallbackKeyboardBound = 'true';
        cartLink.addEventListener('keydown', onFallbackCartKeydown);
      }
    }

    window.customElements?.whenDefined?.('cart-drawer').then(() => {
      const prototype = Object.getPrototypeOf(drawer);

      if (
        drawer.dataset.slFallback !== 'true' ||
        typeof prototype?.open !== 'function'
      ) {
        return;
      }

      document.removeEventListener('keydown', onDocumentKeydown);

      overlay?.removeEventListener('click', close);

      if (cartLink?.dataset.slFallbackKeyboardBound === 'true') {
        cartLink.removeEventListener('keydown', onFallbackCartKeydown);
        delete cartLink.dataset.slFallbackKeyboardBound;
      }

      delete drawer.open;
      delete drawer.close;
      delete drawer.setActiveElement;
      delete drawer.dataset.slFallback;
      delete overlay?.dataset.slFallbackBound;

      drawer.removeAttribute('aria-hidden');
    });

    return true;
  };

  class ScentLabsCartController {
    constructor(drawer) {
      this.drawer = drawer;

      this.mutationAbortController = null;
      this.refreshAbortController = null;
      this.bubbleAbortController = null;

      this.refreshTimer = null;
      this.requestSequence = 0;

      this.isMutating = false;
      this.pendingRefreshOpen = false;
      this.refreshRequestedDuringMutation = false;

      this.discountFeedback = null;

      this.freeShippingState = {
        active:
          drawer.dataset.freeShippingApplicationActive === 'true',
        code:
          drawer.dataset.freeShippingApplicationTitle || ''
      };

      this.lastBubbleRendered = false;

      this.addAbortController = null;
      this.addFormsBusy = new WeakSet();

      this.rewardLevel = Number(drawer.dataset.rewardLevel || 0);
      this.pendingRewardCelebration = null;

      this.giftCleanupInProgress = false;

      this.onClick = this.onClick.bind(this);
      this.onSubmit = this.onSubmit.bind(this);

      this.drawer.addEventListener('submit', this.onSubmit);

      this.patchThemeIntegration();
      this.restoreFreeShippingProgress();

      window.setTimeout(() => {
        void this.initializeCartSafetyState();
      }, 0);
    }

    patchThemeIntegration() {
      this.drawer.getSectionsToRender = () => [
        {
          id: DRAWER_SECTION,
          section: DRAWER_SECTION,
          selector: '#CartDrawer'
        },
        {
          id: BUBBLE_SECTION,
          section: BUBBLE_SECTION,
          selector: '#cart-icon-bubble'
        }
      ];

      this.drawer.renderContents = (parsedState) => {
        void this.renderResponse(parsedState, {
          openAfterRender: true
        });
      };

      this.drawer.refresh = (options = {}) => {
        return this.refresh(options);
      };
    }

    isOpen() {
      return this.drawer.classList.contains('active');
    }

    open(triggeredBy) {
      if (
        triggeredBy &&
        typeof this.drawer.setActiveElement === 'function'
      ) {
        this.drawer.setActiveElement(triggeredBy);
      }

      if (typeof this.drawer.open === 'function') {
        this.drawer.open(triggeredBy);

        window.setTimeout(() => {
          this.drawer
            .querySelector('.sl-cart-drawer__close')
            ?.focus({ preventScroll: true });
        }, 30);

        if (this.pendingRewardCelebration) {
          const level = this.pendingRewardCelebration;

          this.pendingRewardCelebration = null;

          window.setTimeout(() => {
            this.celebrateReward(level);
          }, 380);
        }
      }
    }

    close() {
      if (typeof this.drawer.close === 'function') {
        this.drawer.close();
      }
    }

    onClick(event) {
      const target =
        event.target instanceof Element
          ? event.target
          : event.target?.parentElement;

      const control = target?.closest?.(
        '[data-sl-cart-close], [data-sl-quantity], [data-sl-remove]'
      );

      if (!control || !this.drawer.contains(control)) {
        return false;
      }

      return this.handleCartControl(control);
    }

    handleCartControl(control) {
      if (
        !(control instanceof Element) ||
        !this.drawer.contains(control)
      ) {
        return false;
      }

      if (control.matches('[data-sl-cart-close]')) {
        this.close();
        return true;
      }

      if (control.matches('[data-sl-quantity]')) {
        if (
          control.matches(
            ':disabled, [aria-disabled="true"]'
          )
        ) {
          return true;
        }

        this.handleQuantity(control);
        return true;
      }

      if (control.matches('[data-sl-remove]')) {
        if (
          control.matches(
            ':disabled, [aria-disabled="true"]'
          )
        ) {
          return true;
        }

        const item = control.closest('.sl-cart-item');

        if (!item) {
          return true;
        }

        void this.changeLine(item, 0, 'remove');

        return true;
      }

      return false;
    }

    onSubmit(event) {
      const form = event.target.closest('[data-sl-discount-form]');

      if (!form || !this.drawer.contains(form)) {
        return;
      }

      event.preventDefault();

      void this.applyDiscount(form);
    }

    handleQuantity(button) {
      const item = button.closest('.sl-cart-item');

      if (!item) return;

      const currentQuantity = Number(
        item.dataset.quantity || 0
      );

      const minimum = Math.max(
        1,
        Number(item.dataset.quantityMin || 1)
      );

      const increment = Math.max(
        1,
        Number(item.dataset.quantityIncrement || 1)
      );

      const maximum =
        item.dataset.quantityMax === undefined
          ? Number.POSITIVE_INFINITY
          : Number(item.dataset.quantityMax);

      const canRemove =
        item.dataset.canRemove !== 'false';

      const action = button.dataset.slQuantity;

      let nextQuantity;

      if (action === 'plus') {
        nextQuantity = currentQuantity + increment;

        if (Number.isFinite(maximum)) {
          nextQuantity = Math.min(maximum, nextQuantity);
        }
      } else {
        if (
          currentQuantity <= minimum &&
          !canRemove
        ) {
          return;
        }

        nextQuantity =
          currentQuantity <= minimum
            ? 0
            : Math.max(
                minimum,
                currentQuantity - increment
              );
      }

      if (nextQuantity === currentQuantity) {
        return;
      }

      void this.changeLine(
        item,
        nextQuantity,
        action
      );
    }

    isCartAddForm(form) {
      if (!(form instanceof HTMLFormElement)) {
        return false;
      }

      if (
        form.matches(
          '[data-no-cart-drawer], [data-cart-redirect]'
        )
      ) {
        return false;
      }

      let action;

      try {
        action = new URL(
          form.action || `${getRoot()}cart/add`,
          window.location.href
        );
      } catch (_error) {
        return false;
      }

      const normalizedPath = action.pathname.replace(
        /\/+$/,
        ''
      );

      return normalizedPath.endsWith('/cart/add');
    }

    setProductFormError(form, message = '') {
      const host =
        form.closest('product-form') ||
        form.parentElement ||
        form;

      const wrapper = host.querySelector?.(
        '.product-form__error-message-wrapper'
      );

      const messageElement = host.querySelector?.(
        '.product-form__error-message'
      );

      if (messageElement) {
        messageElement.textContent = message;
      }

      if (wrapper) {
        wrapper.hidden = !message;
      }

      if (!wrapper && message) {
        let fallback = host.querySelector?.(
          '[data-sl-cart-form-error]'
        );

        if (!fallback) {
          fallback = document.createElement('p');

          fallback.setAttribute(
            'data-sl-cart-form-error',
            ''
          );

          fallback.setAttribute(
            'role',
            'alert'
          );

          fallback.style.margin = '10px 0 0';
          fallback.style.fontSize = '13px';
          fallback.style.color = '#b42318';

          form.insertAdjacentElement(
            'afterend',
            fallback
          );
        }

        fallback.textContent = message;
      } else if (!message) {
        host
          .querySelector?.(
            '[data-sl-cart-form-error]'
          )
          ?.remove();
      }
    }

    setProductFormBusy(form, submitter, busy) {
      const button =
        submitter instanceof HTMLElement
          ? submitter
          : form.querySelector(
              'button[type="submit"], input[type="submit"]'
            );

      form.toggleAttribute(
        'aria-busy',
        Boolean(busy)
      );

      if (!button) return;

      if (busy) {
        if (
          button.dataset.slPreviousDisabled === undefined
        ) {
          button.dataset.slPreviousDisabled =
            button.disabled ? 'true' : 'false';
        }

        button.disabled = true;

        button.setAttribute(
          'aria-disabled',
          'true'
        );

        button.classList.add('loading');

        button
          .querySelector?.('.loading__spinner')
          ?.classList.remove('hidden');

        return;
      }

      const wasDisabled =
        button.dataset.slPreviousDisabled === 'true';

      button.disabled = wasDisabled;

      button.setAttribute(
        'aria-disabled',
        wasDisabled ? 'true' : 'false'
      );

      button.classList.remove('loading');

      button
        .querySelector?.('.loading__spinner')
        ?.classList.add('hidden');

      delete button.dataset.slPreviousDisabled;
    }

    async addFromForm(form, submitter) {
      if (
        !this.isCartAddForm(form) ||
        this.addFormsBusy.has(form)
      ) {
        return;
      }

      this.addFormsBusy.add(form);

      this.setProductFormError(form, '');

      this.setProductFormBusy(
        form,
        submitter,
        true
      );

      this.cancelActiveRefresh();

      if (this.addAbortController) {
        this.addAbortController.abort();
      }

      this.addAbortController =
        new AbortController();

      try {
        const formData = new FormData(form);

        formData.delete('return_to');

        formData.set(
          'sections',
          `${DRAWER_SECTION},${BUBBLE_SECTION}`
        );

        formData.set(
          'sections_url',
          getSectionsContext()
        );

        const response = await fetch(
          `${getRoot()}cart/add.js`,
          {
            method: 'POST',
            credentials: 'same-origin',
            headers: {
              Accept: 'application/json',
              'X-Requested-With': 'XMLHttpRequest'
            },
            body: formData,
            signal: this.addAbortController.signal
          }
        );

        let data = null;

        try {
          data = await response.json();
        } catch (_error) {
          data = null;
        }

        if (!response.ok || data?.status) {
          throw new Error(
            data?.description ||
              data?.message ||
              'Produktet kunne ikke tilføjes til kurven.'
          );
        }

        const rendered = await this.renderResponse(
          data,
          {
            openAfterRender: true
          }
        );

        if (!rendered) {
          throw new Error(
            'Produktet blev tilføjet, men kurven kunne ikke vises. Genindlæs siden.'
          );
        }

        this.announce(
          'Produktet blev tilføjet til kurven.'
        );

        const cartState = await this
          .fetchCartState()
          .catch(() => null);

        if (cartState) {
          this.syncFreeShippingProgress(cartState);
        }

        this.dispatchCartUpdated(
          cartState || data
        );
      } catch (error) {
        if (!isAbortError(error)) {
          console.error(
            '[Scent Labs cart] Add to cart failed:',
            error
          );

          this.setProductFormError(
            form,
            error?.message ||
              'Produktet kunne ikke tilføjes. Prøv igen.'
          );
        }
      } finally {
        this.addFormsBusy.delete(form);

        this.setProductFormBusy(
          form,
          submitter,
          false
        );
      }
    }

    cancelActiveRefresh() {
      window.clearTimeout(this.refreshTimer);

      this.refreshTimer = null;
      this.pendingRefreshOpen = false;
      this.refreshRequestedDuringMutation = false;

      this.requestSequence += 1;

      if (this.refreshAbortController) {
        this.refreshAbortController.abort();
        this.refreshAbortController = null;
      }

      if (this.bubbleAbortController) {
        this.bubbleAbortController.abort();
        this.bubbleAbortController = null;
      }
    }

    isFreeGiftItem(item) {
      const properties = item?.properties;

      if (!properties) return false;

      if (Array.isArray(properties)) {
        return properties.some((entry) => {
          if (!entry) return false;

          const key = Array.isArray(entry)
            ? entry[0]
            : entry.name || entry.key;

          const value = Array.isArray(entry)
            ? entry[1]
            : entry.value;

          return (
            key === '_sl_free_gift' &&
            String(value) === 'true'
          );
        });
      }

      return (
        String(
          properties._sl_free_gift || ''
        ) === 'true'
      );
    }

    getEligibleRewardSpend(cart) {
      let spend = Number(
        cart?.total_price || 0
      );

      if (!Array.isArray(cart?.items)) {
        return Math.max(0, spend);
      }

      cart.items.forEach((item) => {
        if (!this.isFreeGiftItem(item)) return;

        spend -= Number(
          item?.final_line_price || 0
        );
      });

      return Math.max(0, spend);
    }

    async enforceGiftEligibility(cart) {
      if (
        !cart ||
        typeof cart !== 'object' ||
        this.giftCleanupInProgress
      ) {
        return cart;
      }

      const minSpend = Number(
        this.drawer.dataset.giftMinSpend || 34900
      );

      if (!Number.isFinite(minSpend) || minSpend <= 0) {
        return cart;
      }

      const paidQty = Array.isArray(cart.items)
        ? cart.items.reduce(
            (sum, item) =>
              this.isFreeGiftItem(item)
                ? sum
                : sum + Number(item?.final_line_price || 0),
            0
          )
        : 0;

      if (paidQty >= minSpend) {
        return cart;
      }

      const giftItems =
        Array.isArray(cart.items)
          ? cart.items.filter((item) =>
              this.isFreeGiftItem(item)
            )
          : [];

      if (!giftItems.length) {
        return cart;
      }

      const updates = {};

      giftItems.forEach((item) => {
        if (item?.key) {
          updates[item.key] = 0;
        }
      });

      if (!Object.keys(updates).length) {
        return cart;
      }

      this.giftCleanupInProgress = true;

      try {
        const cleanedCart =
          await this.cartRequest(
            'update.js',
            {
              updates,
              sections: [
                DRAWER_SECTION,
                BUBBLE_SECTION
              ],
              sections_url:
                getSectionsContext()
            }
          );

        this.announce(
          'Den gratis parfume blev fjernet, fordi der ikke længere er nok dufte i kurven.'
        );

        return cleanedCart;
      } finally {
        this.giftCleanupInProgress = false;
      }
    }

    getRewardLevelFromDrawer() {
      const level = Number(
        this.drawer.dataset.rewardLevel || 0
      );

      if (!Number.isFinite(level)) {
        return 0;
      }

      return Math.max(
        0,
        Math.min(
          3,
          Math.round(level)
        )
      );
    }

    celebrateReward(level) {
      if (
        this.drawer.dataset.confettiEnabled === 'false'
      ) {
        return;
      }

      if (
        window
          .matchMedia?.(
            '(prefers-reduced-motion: reduce)'
          )
          ?.matches
      ) {
        return;
      }

      const safeLevel = Math.max(
        1,
        Math.min(
          3,
          Number(level) || 1
        )
      );

      const bar = this.drawer.querySelector(
        '[data-sl-reward-bar]'
      );

      const node = this.drawer.querySelector(
        `[data-sl-reward-node="${safeLevel}"]`
      );

      const layer = bar?.querySelector(
        '[data-sl-confetti-layer]'
      );

      if (!bar || !node || !layer) {
        return;
      }

      node.classList.remove(
        'is-celebrating'
      );

      void node.offsetWidth;

      node.classList.add(
        'is-celebrating'
      );

      window.setTimeout(() => {
        node.classList.remove(
          'is-celebrating'
        );
      }, 700);

      const nodeRect =
        node.getBoundingClientRect();

      const layerRect =
        layer.getBoundingClientRect();

      const originX =
        nodeRect.left +
        nodeRect.width / 2 -
        layerRect.left;

      const originY =
        nodeRect.top +
        nodeRect.height / 2 -
        layerRect.top;

      const particleCount =
        window.innerWidth <= 749
          ? 20
          : 24;

      for (
        let index = 0;
        index < particleCount;
        index += 1
      ) {
        const particle =
          document.createElement('i');

        particle.className =
          'sl-reward-confetti';

        const horizontal =
          (Math.random() - 0.5) * 132;

        const peak =
          -(34 + Math.random() * 62);

        const end =
          peak +
          58 +
          Math.random() * 34;

        const rotation =
          (Math.random() - 0.5) * 620;

        const delay =
          Math.random() * 90;

        particle.style.left =
          `${originX}px`;

        particle.style.top =
          `${originY}px`;

        particle.style.setProperty(
          '--sl-dx',
          `${horizontal.toFixed(1)}px`
        );

        particle.style.setProperty(
          '--sl-dy',
          `${peak.toFixed(1)}px`
        );

        particle.style.setProperty(
          '--sl-dy-end',
          `${end.toFixed(1)}px`
        );

        particle.style.setProperty(
          '--sl-rot',
          `${rotation.toFixed(1)}deg`
        );

        particle.style.setProperty(
          '--sl-delay',
          `${delay.toFixed(0)}ms`
        );

        if (Math.random() > 0.55) {
          particle.style.width = '7px';
          particle.style.height = '5px';
        }

        layer.appendChild(particle);

        window.setTimeout(() => {
          particle.remove();
        }, 1150 + delay);
      }
    }

    async initializeCartSafetyState() {
      try {
        let cart =
          await this.fetchCartState();

        const cleaned =
          await this.enforceGiftEligibility(cart);

        if (cleaned !== cart) {
          cart = cleaned;

          await this.renderResponse(
            cart,
            {
              openAfterRender: false
            }
          );
        }

        this.syncFreeShippingProgress(cart);

        this.rewardLevel =
          this.getRewardLevelFromDrawer();
      } catch (error) {
        if (!isAbortError(error)) {
          console.warn(
            '[Scent Labs cart] Initial cart safety check failed:',
            error
          );
        }
      }
    }

    setItemBusy(item, busy) {
      if (!item) return;

      item.setAttribute(
        'aria-busy',
        busy ? 'true' : 'false'
      );
    }

    setDrawerBusy(busy) {
      const wasMutating =
        this.isMutating;

      this.isMutating =
        Boolean(busy);

      this.drawer.setAttribute(
        'aria-busy',
        busy ? 'true' : 'false'
      );

      this.drawer
        .querySelectorAll(
          '[data-sl-quantity], [data-sl-remove], [data-sl-discount-form] button, [data-sl-discount-form] input, #CartDrawer-Checkout'
        )
        .forEach((control) => {
          if (busy) {
            if (
              control.dataset
                .slBusyPreviousDisabled === undefined
            ) {
              control.dataset.slBusyPreviousDisabled =
                control.disabled
                  ? 'true'
                  : 'false';
            }

            control.disabled = true;

            return;
          }

          const wasDisabled =
            control.dataset
              .slBusyPreviousDisabled === 'true';

          control.disabled = wasDisabled;

          delete control.dataset
            .slBusyPreviousDisabled;
        });

      if (
        !busy &&
        wasMutating &&
        this.refreshRequestedDuringMutation
      ) {
        const shouldOpen =
          this.pendingRefreshOpen;

        this.refreshRequestedDuringMutation =
          false;

        this.pendingRefreshOpen =
          false;

        this.scheduleRefresh({
          openAfterRender:
            shouldOpen
        });
      }
    }

    async changeLine(
      item,
      quantity,
      focusAction
    ) {
      if (this.isMutating) return;

      const line = Number(
        item.dataset.line || 0
      );

      const lineKey =
        item.dataset.lineKey || '';

      if (
        !Number.isInteger(line) ||
        line < 1
      ) {
        return;
      }

      this.setDrawerBusy(true);

      this.cancelActiveRefresh();

      this.setItemBusy(
        item,
        true
      );

      try {
        const payload = {
          quantity,
          sections: [
            DRAWER_SECTION,
            BUBBLE_SECTION
          ],
          sections_url:
            getSectionsContext()
        };

        let response;

        if (lineKey) {
          try {
            response =
              await this.cartRequest(
                'change.js',
                {
                  id: lineKey,
                  ...payload
                }
              );
          } catch (error) {
            if (isAbortError(error)) {
              throw error;
            }

            response =
              await this.cartRequest(
                'change.js',
                {
                  line,
                  ...payload
                }
              );
          }
        } else {
          response =
            await this.cartRequest(
              'change.js',
              {
                line,
                ...payload
              }
            );
        }

        response =
          await this.enforceGiftEligibility(
            response
          );

        this.revalidateDiscountFeedback(
          response
        );

        this.syncFreeShippingProgress(
          response
        );

        const rendered =
          await this.renderResponse(
            response,
            {
              openAfterRender: false
            }
          );

        if (!rendered) {
          this.announce(
            'Kurven blev opdateret, men visningen kunne ikke synkroniseres. Genindlæs siden.'
          );

          this.dispatchCartUpdated(
            response
          );

          return;
        }

        this.restoreMutationFocus(
          lineKey,
          line,
          focusAction,
          quantity
        );

        this.announce(
          quantity === 0
            ? 'Produktet blev fjernet fra kurven.'
            : 'Antallet blev opdateret.'
        );

        this.dispatchCartUpdated(
          response
        );
      } catch (error) {
        if (!isAbortError(error)) {
          console.error(
            '[Scent Labs cart] Quantity update failed:',
            error
          );

          this.setItemBusy(
            item,
            false
          );

          this.announce(
            error.message ||
              'Kurven kunne ikke opdateres. Prøv igen.'
          );
        }
      } finally {
        if (item?.isConnected) {
          this.setItemBusy(
            item,
            false
          );
        }

        this.setDrawerBusy(false);
      }
    }

    restoreMutationFocus(
      lineKey,
      line,
      action,
      quantity
    ) {
      window.setTimeout(() => {
        if (quantity > 0) {
          const keyedItem =
            lineKey
              ? Array.from(
                  this.drawer.querySelectorAll(
                    '[data-line-key]'
                  )
                ).find(
                  (candidate) =>
                    candidate.dataset.lineKey ===
                    lineKey
                )
              : null;

          const positionalItem =
            this.drawer.querySelector(
              `[data-line="${line}"]`
            );

          const item =
            keyedItem || positionalItem;

          const target =
            item?.querySelector(
              `[data-sl-quantity="${action}"]`
            ) ||
            item?.querySelector(
              '[data-sl-quantity="plus"]'
            );

          if (target) {
            target.focus({
              preventScroll: true
            });

            return;
          }
        }

        this.drawer
          .querySelector(
            '.sl-cart-drawer__close'
          )
          ?.focus({
            preventScroll: true
          });
      }, 30);
    }

    async applyDiscount(form) {
      if (this.isMutating) return;

      const input = form.querySelector(
        'input[name="discount"]'
      );

      const button = form.querySelector(
        'button[type="submit"]'
      );

      const status = form.querySelector(
        '[data-sl-discount-status]'
      );

      const code =
        input?.value?.trim();

      if (!code) {
        this.setDiscountStatus(
          status,
          'Indtast en rabatkode.',
          'error'
        );

        input?.focus();

        return;
      }

      const previousTotal = Number(
        this.drawer.dataset.cartTotal || 0
      );

      const defaultButtonLabel =
        button?.dataset.defaultLabel ||
        'Anvend';

      this.setDrawerBusy(true);

      this.cancelActiveRefresh();

      if (button) {
        button.textContent =
          'Arbejder…';
      }

      this.setDiscountStatus(
        status,
        '',
        ''
      );

      try {
        const currentCart =
          await this.fetchCartState();

        const discountCodes =
          this.mergeDiscountCodes(
            currentCart,
            code
          );

        let response =
          await this.cartRequest(
            'update.js',
            {
              discount:
                discountCodes.join(','),
              sections: [
                DRAWER_SECTION,
                BUBBLE_SECTION
              ],
              sections_url:
                getSectionsContext()
            }
          );

        response =
          await this.enforceGiftEligibility(
            response
          );

        const result =
          this.classifyDiscountResult(
            response,
            code,
            previousTotal,
            true
          );

        this.discountFeedback =
          this.getDiscountFeedback(
            result,
            code
          );

        if (
          result === 'applied' &&
          this.isConfiguredFreeShippingCode(
            code
          )
        ) {
          this.discountFeedback.message =
            'Rabatkoden er anvendt. Gratis fragt er aktiveret.';
        }

        this.syncFreeShippingProgress(
          response,
          {
            codeHint: code,
            result
          }
        );

        const rendered =
          await this.renderResponse(
            response,
            {
              openAfterRender: false
            }
          );

        if (!rendered) {
          this.announce(
            'Rabatkoden blev sendt til Shopify, men kurvvisningen kunne ikke synkroniseres. Genindlæs siden.'
          );

          this.dispatchCartUpdated(
            response
          );

          return;
        }

        this.restoreDiscountFeedback();

        this.dispatchCartUpdated(
          response
        );

        window.setTimeout(() => {
          this.drawer
            .querySelector(
              '[data-sl-discount-form] button[type="submit"]'
            )
            ?.focus({
              preventScroll: true
            });
        }, 0);
      } catch (error) {
        if (!isAbortError(error)) {
          console.error(
            '[Scent Labs cart] Discount update failed:',
            error
          );

          if (button) {
            button.textContent =
              defaultButtonLabel;
          }

          this.setDiscountStatus(
            status,
            error.message ||
              'Rabatkoden kunne ikke anvendes.',
            'error'
          );
        }
      } finally {
        this.setDrawerBusy(false);
      }
    }

    mergeDiscountCodes(
      cart,
      newCode
    ) {
      const codes =
        Array.isArray(
          cart?.discount_codes
        )
          ? cart.discount_codes
              .filter(
                (entry) =>
                  entry?.applicable !== false
              )
              .map(
                (entry) =>
                  String(
                    entry?.code || ''
                  ).trim()
              )
              .filter(Boolean)
          : [];

      codes.push(newCode);

      const uniqueCodes = [];
      const seen = new Set();

      codes.forEach((code) => {
        const normalized =
          code.toLocaleLowerCase(
            'da-DK'
          );

        if (seen.has(normalized)) {
          return;
        }

        seen.add(normalized);

        uniqueCodes.push(code);
      });

      return uniqueCodes;
    }

    classifyDiscountResult(
      cart,
      code,
      previousTotal,
      allowTotalComparison = true
    ) {
      if (
        !cart ||
        typeof cart !== 'object'
      ) {
        return 'registered';
      }

      const normalizedCode =
        code.toLocaleLowerCase(
          'da-DK'
        );

      const matchingCode =
        Array.isArray(
          cart.discount_codes
        )
          ? cart.discount_codes.find(
              (entry) =>
                String(
                  entry?.code || ''
                ).toLocaleLowerCase(
                  'da-DK'
                ) === normalizedCode
            )
          : null;

      if (
        matchingCode?.applicable === false
      ) {
        return 'rejected';
      }

      if (
        matchingCode &&
        matchingCode.applicable !== false
      ) {
        return 'applied';
      }

      const titles = [];

      (
        cart.discount_applications || []
      ).forEach((application) => {
        if (application?.title) {
          titles.push(
            application.title
          );
        }
      });

      (
        cart.cart_level_discount_applications ||
        []
      ).forEach((application) => {
        if (application?.title) {
          titles.push(
            application.title
          );
        }
      });

      (
        cart.items || []
      ).forEach((item) => {
        (
          item.line_level_discount_allocations ||
          []
        ).forEach((allocation) => {
          if (
            allocation
              ?.discount_application
              ?.title
          ) {
            titles.push(
              allocation
                .discount_application
                .title
            );
          }
        });
      });

      if (
        titles.some((title) =>
          String(title)
            .toLocaleLowerCase(
              'da-DK'
            )
            .includes(normalizedCode)
        )
      ) {
        return 'applied';
      }

      if (
        allowTotalComparison &&
        Number.isFinite(previousTotal) &&
        Number(cart.total_price) <
          previousTotal
      ) {
        return 'applied';
      }

      return 'registered';
    }

    getDiscountFeedback(
      result,
      code
    ) {
      if (result === 'rejected') {
        return {
          code,
          type: 'error',
          message:
            'Rabatkoden er ikke gyldig for denne kurv.'
        };
      }

      if (result === 'applied') {
        return {
          code,
          type: 'success',
          message:
            'Rabatkoden er anvendt.'
        };
      }

      return {
        code,
        type: 'neutral',
        message:
          'Shopify kunne ikke bekræfte koden i kurven. Den valideres igen ved checkout.'
      };
    }

    normalizeDiscountCode(code) {
      return String(
        code || ''
      )
        .trim()
        .toLocaleLowerCase(
          'da-DK'
        );
    }

    getConfiguredFreeShippingCodes() {
      if (
        this.drawer.dataset
          .freeShippingCodeReaction ===
        'false'
      ) {
        return [];
      }

      const rawCodes =
        this.drawer.dataset
          .freeShippingDiscountCodes || '';

      const uniqueCodes = [];
      const seen = new Set();

      rawCodes
        .split(/[,;\n]+/)
        .map((code) =>
          String(
            code || ''
          ).trim()
        )
        .filter(Boolean)
        .forEach((code) => {
          const normalized =
            this.normalizeDiscountCode(code);

          if (
            !normalized ||
            seen.has(normalized)
          ) {
            return;
          }

          seen.add(normalized);

          uniqueCodes.push({
            code,
            normalized
          });
        });

      return uniqueCodes;
    }

    isConfiguredFreeShippingCode(code) {
      const normalized =
        this.normalizeDiscountCode(code);

      if (!normalized) {
        return false;
      }

      return this
        .getConfiguredFreeShippingCodes()
        .some(
          (entry) =>
            entry.normalized ===
            normalized
        );
    }

    getShippingDiscountApplications(
      cart
    ) {
      const applications = [
        ...(
          Array.isArray(
            cart?.discount_applications
          )
            ? cart.discount_applications
            : []
        ),
        ...(
          Array.isArray(
            cart?.cart_level_discount_applications
          )
            ? cart.cart_level_discount_applications
            : []
        )
      ];

      return applications.filter(
        (application) => {
          const targetType = String(
            application?.target_type ||
              application?.targetType ||
              ''
          ).toLocaleLowerCase(
            'en-US'
          );

          return (
            targetType ===
              'shipping_line' ||
            targetType ===
              'shippingline'
          );
        }
      );
    }

    findActiveFreeShippingCode(
      cart,
      {
        codeHint = '',
        result = ''
      } = {}
    ) {
      if (
        this.drawer.dataset
          .freeShippingCodeReaction ===
        'false'
      ) {
        return '';
      }

      const configured =
        this.getConfiguredFreeShippingCodes();

      const configuredMap =
        new Map(
          configured.map((entry) => [
            entry.normalized,
            entry.code
          ])
        );

      const applicableCodes =
        Array.isArray(
          cart?.discount_codes
        )
          ? cart.discount_codes.filter(
              (entry) =>
                entry?.applicable !==
                false
            )
          : [];

      for (
        const entry of applicableCodes
      ) {
        const code = String(
          entry?.code || ''
        ).trim();

        const normalized =
          this.normalizeDiscountCode(code);

        if (
          configuredMap.has(normalized)
        ) {
          return (
            code ||
            configuredMap.get(
              normalized
            )
          );
        }
      }

      const shippingApplications =
        this.getShippingDiscountApplications(
          cart
        );

      for (
        const application of shippingApplications
      ) {
        const title = String(
          application?.title || ''
        ).trim();

        const normalizedTitle =
          this.normalizeDiscountCode(
            title
          );

        if (
          configuredMap.has(
            normalizedTitle
          )
        ) {
          return (
            title ||
            configuredMap.get(
              normalizedTitle
            )
          );
        }

        const matchingConfigured =
          configured.find((entry) =>
            normalizedTitle.includes(
              entry.normalized
            )
          );

        if (matchingConfigured) {
          return matchingConfigured.code;
        }
      }

      if (
        shippingApplications.length >
          0 &&
        configured.length === 1
      ) {
        return configured[0].code;
      }

      if (
        result === 'applied' &&
        codeHint &&
        this.isConfiguredFreeShippingCode(
          codeHint
        )
      ) {
        return String(
          codeHint
        ).trim();
      }

      return '';
    }

    formatFreeShippingCodeMessage(code) {
      const template = (
        this.drawer.dataset
          .freeShippingCodeMessage ||
        'Gratis fragt er aktiveret med rabatkoden [code]'
      ).trim();

      return template
        .split('[code]')
        .join(
          String(
            code || ''
          ).trim()
        );
    }

    updateFreeShippingProgress(
      active,
      code = ''
    ) {
      const container =
        this.drawer.querySelector(
          '[data-sl-free-shipping-progress]'
        );

      if (!container) return;

      const thresholdMessage =
        container.querySelector(
          '[data-sl-free-shipping-threshold-message]'
        );

      const codeMessage =
        container.querySelector(
          '[data-sl-free-shipping-code-message]'
        );

      const track =
        container.querySelector(
          '[data-sl-free-shipping-track]'
        );

      const fill =
        container.querySelector(
          '[data-sl-free-shipping-fill]'
        );

      const thresholdComplete =
        container.dataset
          .thresholdComplete === 'true';

      const thresholdProgress =
        Number(
          container.dataset
            .thresholdProgress || 0
        );

      const thresholdNow = String(
        container.dataset.thresholdNow ||
          '0'
      );

      const thresholdText =
        container.dataset.thresholdText ||
        '';

      container.classList.toggle(
        'is-code-active',
        Boolean(active)
      );

      container.classList.toggle(
        'is-complete',
        Boolean(active) ||
          thresholdComplete
      );

      if (thresholdMessage) {
        thresholdMessage.hidden =
          Boolean(active);
      }

      if (codeMessage) {
        codeMessage.hidden = !active;

        if (active) {
          codeMessage.textContent =
            this.formatFreeShippingCodeMessage(
              code
            );
        }
      }

      if (fill) {
        fill.style.width = active
          ? '100%'
          : `${Math.max(
              0,
              Math.min(
                100,
                thresholdProgress
              )
            )}%`;
      }

      if (track) {
        const threshold = String(
          this.drawer.dataset
            .freeShippingThreshold || '0'
        );

        track.setAttribute(
          'aria-valuenow',
          active
            ? threshold
            : thresholdNow
        );

        track.setAttribute(
          'aria-valuetext',
          active
            ? 'Gratis fragt aktiveret'
            : thresholdText
        );

        track.setAttribute(
          'aria-label',
          active
            ? 'Gratis fragt aktiveret med rabatkode'
            : 'Fremskridt mod gratis fragt'
        );
      }
    }

    syncFreeShippingProgress(
      cart,
      options = {}
    ) {
      if (
        this.drawer.dataset
          .freeShippingCodeReaction ===
        'false'
      ) {
        this.freeShippingState = {
          active: false,
          code: ''
        };

        this.updateFreeShippingProgress(
          false
        );

        return;
      }

      const activeCode =
        this.findActiveFreeShippingCode(
          cart,
          options
        );

      const hasExplicitCodeState =
        Array.isArray(
          cart?.discount_codes
        );

      const hasShippingApplications =
        this.getShippingDiscountApplications(
          cart
        ).length > 0;

      const resultIsRejected =
        options?.result === 'rejected';

      if (activeCode) {
        this.freeShippingState = {
          active: true,
          code: activeCode
        };
      } else if (
        hasExplicitCodeState ||
        hasShippingApplications ||
        resultIsRejected
      ) {
        this.freeShippingState = {
          active: false,
          code: ''
        };
      }

      this.restoreFreeShippingProgress();
    }

    restoreFreeShippingProgress() {
      const liquidActive =
        this.drawer.dataset
          .freeShippingApplicationActive ===
        'true';

      const liquidCode =
        this.drawer.dataset
          .freeShippingApplicationTitle ||
        '';

      if (liquidActive) {
        this.freeShippingState = {
          active: true,
          code:
            liquidCode ||
            this.freeShippingState?.code ||
            ''
        };
      }

      this.updateFreeShippingProgress(
        Boolean(
          this.freeShippingState
            ?.active
        ),
        this.freeShippingState?.code ||
          ''
      );
    }

    async initializeFreeShippingState() {
      if (
        this.drawer.dataset
          .freeShippingCodeReaction ===
        'false'
      ) {
        return;
      }

      try {
        const cart =
          await this.fetchCartState();

        this.syncFreeShippingProgress(
          cart
        );
      } catch (error) {
        console.warn(
          '[Scent Labs cart] Free shipping code state could not be read:',
          error
        );
      }
    }

    revalidateDiscountFeedback(cart) {
      if (
        !this.discountFeedback?.code
      ) {
        return;
      }

      const result =
        this.classifyDiscountResult(
          cart,
          this.discountFeedback.code,
          Number.NaN,
          false
        );

      this.discountFeedback =
        this.getDiscountFeedback(
          result,
          this.discountFeedback.code
        );
    }

    restoreDiscountFeedback() {
      if (!this.discountFeedback) {
        return;
      }

      const input =
        this.drawer.querySelector(
          'input[name="discount"]'
        );

      const status =
        this.drawer.querySelector(
          '[data-sl-discount-status]'
        );

      if (input) {
        input.value =
          this.discountFeedback.code;
      }

      this.setDiscountStatus(
        status,
        this.discountFeedback.message,
        this.discountFeedback.type
      );

      const details =
        status?.closest(
          'details.sl-cart-discount'
        );

      if (details) {
        details.open = true;
      }
    }

    setDiscountStatus(
      element,
      message,
      type
    ) {
      if (!element) return;

      element.textContent = message;

      element.classList.toggle(
        'is-error',
        type === 'error'
      );

      element.classList.toggle(
        'is-success',
        type === 'success'
      );

      const input = element
        .closest(
          '[data-sl-discount-form]'
        )
        ?.querySelector(
          'input[name="discount"]'
        );

      if (input) {
        input.setAttribute(
          'aria-invalid',
          type === 'error'
            ? 'true'
            : 'false'
        );
      }
    }

    async fetchCartState() {
      const response = await fetch(
        `${getRoot()}cart.js`,
        {
          method: 'GET',
          credentials: 'same-origin',
          headers: {
            Accept: 'application/json'
          },
          cache: 'no-store'
        }
      );

      if (!response.ok) {
        throw new Error(
          'Kurven kunne ikke læses.'
        );
      }

      return response.json();
    }

    async cartRequest(
      endpoint,
      payload
    ) {
      if (
        this.mutationAbortController
      ) {
        this.mutationAbortController.abort();
      }

      this.mutationAbortController =
        new AbortController();

      const response = await fetch(
        `${getRoot()}cart/${endpoint}`,
        {
          method: 'POST',
          credentials: 'same-origin',
          headers: {
            'Content-Type':
              'application/json',
            Accept:
              'application/json'
          },
          body: JSON.stringify(payload),
          signal:
            this.mutationAbortController
              .signal
        }
      );

      let data = null;

      try {
        data = await response.json();
      } catch (_error) {
        data = null;
      }

      if (!response.ok) {
        const message =
          data?.description ||
          data?.message ||
          'Kurven kunne ikke opdateres.';

        throw new Error(message);
      }

      if (
        !data ||
        typeof data !== 'object'
      ) {
        throw new Error(
          'Shopify returnerede et ugyldigt kurvsvar.'
        );
      }

      return data;
    }

    async refresh({
      openAfterRender = false
    } = {}) {
      const sequence =
        ++this.requestSequence;

      if (
        this.refreshAbortController
      ) {
        this.refreshAbortController.abort();
      }

      this.refreshAbortController =
        new AbortController();

      try {
        const response = await fetch(
          getSectionsUrl(),
          {
            method: 'GET',
            credentials: 'same-origin',
            headers: {
              Accept:
                'application/json'
            },
            cache: 'no-store',
            signal:
              this.refreshAbortController
                .signal
          }
        );

        if (!response.ok) {
          throw new Error(
            'Kurven kunne ikke indlæses.'
          );
        }

        const sections =
          await response.json();

        if (
          sequence !==
          this.requestSequence
        ) {
          return false;
        }

        const rendered =
          this.renderSections(
            sections,
            {
              openAfterRender
            }
          );

        if (!rendered) {
          throw new Error(
            'Shopify kunne ikke rendere kurvsektionen.'
          );
        }

        if (
          !this.lastBubbleRendered
        ) {
          await this.refreshCartBubble(
            sequence
          );
        }

        const cartState =
          await this
            .fetchCartState()
            .catch(() => null);

        if (
          cartState &&
          sequence ===
            this.requestSequence
        ) {
          this.syncFreeShippingProgress(
            cartState
          );
        }

        return true;
      } catch (error) {
        if (!isAbortError(error)) {
          console.error(
            '[Scent Labs cart] Refresh failed:',
            error
          );

          this.announce(
            'Kurven kunne ikke opdateres. Genindlæs siden og prøv igen.'
          );
        }

        return false;
      }
    }

    scheduleRefresh({
      openAfterRender = false
    } = {}) {
      this.pendingRefreshOpen =
        this.pendingRefreshOpen ||
        Boolean(openAfterRender);

      if (this.isMutating) {
        this.refreshRequestedDuringMutation =
          true;

        return;
      }

      window.clearTimeout(
        this.refreshTimer
      );

      this.refreshTimer =
        window.setTimeout(() => {
          const shouldOpen =
            this.pendingRefreshOpen;

          this.pendingRefreshOpen =
            false;

          this.refreshTimer = null;

          void this.refresh({
            openAfterRender:
              shouldOpen
          });
        }, 70);
    }

    async renderResponse(
      response,
      {
        openAfterRender = false
      } = {}
    ) {
      if (
        response?.sections &&
        typeof response.sections ===
          'object'
      ) {
        const rendered =
          this.renderSections(
            response.sections,
            {
              openAfterRender
            }
          );

        if (rendered) {
          this.syncFreeShippingProgress(
            response
          );

          if (
            !this.lastBubbleRendered
          ) {
            await this.refreshCartBubble(
              this.requestSequence
            );
          }

          return true;
        }
      }

      return this.refresh({
        openAfterRender
      });
    }

    async refreshCartBubble(
      expectedSequence =
        this.requestSequence
    ) {
      if (
        this.bubbleAbortController
      ) {
        this.bubbleAbortController.abort();
      }

      this.bubbleAbortController =
        new AbortController();

      try {
        const response = await fetch(
          getSingleSectionUrl(
            BUBBLE_SECTION
          ),
          {
            method: 'GET',
            credentials: 'same-origin',
            headers: {
              Accept: 'text/html'
            },
            cache: 'no-store',
            signal:
              this.bubbleAbortController
                .signal
          }
        );

        if (!response.ok) {
          throw new Error(
            'Kurvikonet kunne ikke indlæses.'
          );
        }

        const html =
          await response.text();

        if (
          expectedSequence !==
          this.requestSequence
        ) {
          return false;
        }

        return this.replaceCartBubble(
          html
        );
      } catch (error) {
        if (!isAbortError(error)) {
          console.warn(
            '[Scent Labs cart] Cart bubble fallback failed:',
            error
          );
        }

        return false;
      }
    }

    renderSections(
      sections,
      {
        openAfterRender = false
      } = {}
    ) {
      if (
        !sections ||
        typeof sections !== 'object'
      ) {
        return false;
      }

      const drawerHTML =
        sections[DRAWER_SECTION];

      if (
        typeof drawerHTML !==
          'string' ||
        !drawerHTML.trim()
      ) {
        return false;
      }

      const wasOpen =
        this.isOpen();

      const replaced =
        this.replaceDrawer(
          drawerHTML
        );

      if (!replaced) {
        return false;
      }

      this.lastBubbleRendered = false;
      this.addAbortController = null;
      this.addFormsBusy =
        new WeakSet();

      const bubbleHTML =
        sections[BUBBLE_SECTION];

      if (
        typeof bubbleHTML ===
          'string' &&
        bubbleHTML.trim()
      ) {
        this.lastBubbleRendered =
          this.replaceCartBubble(
            bubbleHTML
          );
      }

      if (
        openAfterRender &&
        !wasOpen
      ) {
        window.setTimeout(
          () => this.open(),
          0
        );
      }

      return true;
    }

    restoreNativeFocusTrap() {
      if (
        this.drawer.dataset.slFallback ===
        'true'
      ) {
        return;
      }

      if (
        typeof trapFocus !==
          'function' ||
        !this.isOpen()
      ) {
        return;
      }

      const container =
        this.drawer.classList.contains(
          'is-empty'
        )
          ? this.drawer.querySelector(
              '.drawer__inner-empty'
            )
          : this.drawer.querySelector(
              '#CartDrawer'
            );

      const focusElement =
        this.drawer.querySelector(
          '.drawer__inner'
        ) ||
        this.drawer.querySelector(
          '.sl-cart-drawer__close'
        );

      if (
        !container ||
        !focusElement
      ) {
        return;
      }

      window.requestAnimationFrame(
        () => {
          if (this.isOpen()) {
            trapFocus(
              container,
              focusElement
            );
          }
        }
      );
    }

    replaceDrawer(html) {
      const parsed = parseHTML(html);

      const newDrawer =
        parsed?.querySelector(
          DRAWER_SELECTOR
        );

      const newInner =
        newDrawer?.querySelector(
          '.sl-cart-drawer__inner'
        );

      const currentInner =
        this.drawer.querySelector(
          '.sl-cart-drawer__inner'
        );

      if (
        !newDrawer ||
        !newInner ||
        !currentInner
      ) {
        return false;
      }

      const previousRewardLevel =
        this.getRewardLevelFromDrawer();

      const bodyScrollTop =
        this.drawer.querySelector(
          '.sl-cart-drawer__body'
        )?.scrollTop || 0;

      const footerScrollTop =
        this.drawer.querySelector(
          '.sl-cart-drawer__footer'
        )?.scrollTop || 0;

      currentInner.replaceWith(
        newInner
      );

      this.drawer.classList.toggle(
        'is-empty',
        newDrawer.classList.contains(
          'is-empty'
        )
      );

      [
        'data-section-id',
        'data-cart-total',
        'data-display-total',
        'data-reference-total',
        'data-cart-count',
        'data-cart-currency',
        'data-free-shipping-threshold',
        'data-free-shipping-code-reaction',
        'data-free-shipping-discount-codes',
        'data-free-shipping-code-message',
        'data-free-shipping-application-active',
        'data-free-shipping-application-title',
        'data-reward-level',
        'data-reward-1-threshold',
        'data-reward-2-threshold',
        'data-reward-3-threshold',
        'data-gift-min-spend',
        'data-confetti-enabled'
      ].forEach((name) => {
        const value =
          newDrawer.getAttribute(name);

        if (value === null) {
          this.drawer.removeAttribute(
            name
          );
        } else {
          this.drawer.setAttribute(
            name,
            value
          );
        }
      });

      const body =
        this.drawer.querySelector(
          '.sl-cart-drawer__body'
        );

      const footer =
        this.drawer.querySelector(
          '.sl-cart-drawer__footer'
        );

      if (body) {
        body.scrollTop =
          bodyScrollTop;
      }

      if (footer) {
        footer.scrollTop =
          footerScrollTop;
      }

      const nextRewardLevel =
        this.getRewardLevelFromDrawer();

      this.rewardLevel =
        nextRewardLevel;

      if (
        nextRewardLevel >
        previousRewardLevel
      ) {
        if (this.isOpen()) {
          window.setTimeout(() => {
            this.celebrateReward(
              nextRewardLevel
            );
          }, 90);
        } else {
          this.pendingRewardCelebration =
            nextRewardLevel;
        }
      }

      this.restoreDiscountFeedback();
      this.restoreFreeShippingProgress();
      this.restoreNativeFocusTrap();

      if (this.isMutating) {
        this.setDrawerBusy(true);
      }

      return true;
    }

    replaceCartBubble(html) {
      const parsed = parseHTML(html);

      const current =
        document.querySelector(
          '#cart-icon-bubble'
        );

      if (!parsed || !current) {
        return false;
      }

      const replacement =
        parsed.querySelector(
          '#cart-icon-bubble'
        );

      if (replacement) {
        current.innerHTML =
          replacement.innerHTML;

        return true;
      }

      const sectionWrapper =
        parsed.querySelector(
          '.shopify-section'
        );

      if (!sectionWrapper) {
        return false;
      }

      current.innerHTML =
        sectionWrapper.innerHTML;

      return true;
    }

    announce(message) {
      const region =
        this.drawer.querySelector(
          '[data-sl-cart-live]'
        );

      if (!region) return;

      region.textContent = '';

      window.setTimeout(() => {
        region.textContent =
          message;
      }, 20);
    }

    dispatchCartUpdated(cart) {
      const detail = {
        cart,
        source:
          'scentlabs-cart-drawer'
      };

      document.dispatchEvent(
        new CustomEvent(
          'cart:updated',
          {
            detail
          }
        )
      );

      try {
        if (
          typeof publish ===
            'function' &&
          typeof PUB_SUB_EVENTS ===
            'object' &&
          PUB_SUB_EVENTS?.cartUpdate
        ) {
          publish(
            PUB_SUB_EVENTS.cartUpdate,
            {
              source:
                'scentlabs-cart-drawer',
              cartData: cart
            }
          );
        }
      } catch (error) {
        console.warn(
          '[Scent Labs cart] Theme cart event could not be published:',
          error
        );
      }
    }
  }

  const enhanceDrawer = (drawer) => {
    if (!drawer) return null;

    if (controllers.has(drawer)) {
      return controllers.get(drawer);
    }

    installFallbackDrawerAPI(drawer);

    const controller =
      new ScentLabsCartController(
        drawer
      );

    controllers.set(
      drawer,
      controller
    );

    return controller;
  };

  const getController = () => {
    return enhanceDrawer(
      document.querySelector(
        DRAWER_SELECTOR
      )
    );
  };

  const initialize = () => {
    getController();

    window.addEventListener(
      'click',
      (event) => {
        const target =
          event.target instanceof Element
            ? event.target
            : event.target?.parentElement;

        if (!target) return;

        const carouselButton =
          target.closest(
            `${DRAWER_SELECTOR} [data-sl-carousel-prev], ` +
              `${DRAWER_SELECTOR} [data-sl-carousel-next]`
          );

        if (carouselButton) {
          const carousel =
            carouselButton.closest(
              '[data-sl-carousel]'
            );

          const track =
            carousel?.querySelector(
              '[data-sl-carousel-track]'
            );

          if (track) {
            event.preventDefault();
            event.stopImmediatePropagation();

            const direction =
              carouselButton.hasAttribute(
                'data-sl-carousel-prev'
              )
                ? -1
                : 1;

            const amount =
              Math.max(
                180,
                Math.round(
                  track.clientWidth * 0.78
                )
              );

            track.scrollBy({
              left:
                direction * amount,
              behavior: 'smooth'
            });
          }

          return;
        }

        const drawerControl =
          target.closest(
            `${DRAWER_SELECTOR} [data-sl-cart-close], ` +
              `${DRAWER_SELECTOR} [data-sl-quantity], ` +
              `${DRAWER_SELECTOR} [data-sl-remove]`
          );

        if (drawerControl) {
          const drawer =
            drawerControl.closest(
              DRAWER_SELECTOR
            );

          const controller =
            enhanceDrawer(drawer);

          if (!controller) return;

          event.preventDefault();
          event.stopImmediatePropagation();

          controller.handleCartControl(
            drawerControl
          );

          return;
        }

        const headerTrigger =
          target.closest(
            '#cart-icon-bubble, .header__icon--cart'
          );

        if (
          headerTrigger &&
          !headerTrigger.closest(
            DRAWER_SELECTOR
          )
        ) {
          const controller =
            getController();

          if (!controller) return;

          event.preventDefault();
          event.stopImmediatePropagation();

          controller.open(
            headerTrigger
          );

          controller.scheduleRefresh({
            openAfterRender: false
          });

          return;
        }

        const openTrigger =
          target.closest(
            '[data-open-cart], [data-cart-drawer-toggle], [aria-controls="CartDrawer"]'
          );

        if (
          !openTrigger ||
          openTrigger.closest(
            DRAWER_SELECTOR
          )
        ) {
          return;
        }

        const controller =
          getController();

        if (!controller) return;

        event.preventDefault();
        event.stopImmediatePropagation();

        controller.open(openTrigger);

        controller.scheduleRefresh({
          openAfterRender: false
        });
      },
      true
    );

    window.addEventListener(
      'submit',
      (event) => {
        const form = event.target;

        const controller =
          getController();

        if (
          !controller ||
          !(
            form instanceof
            HTMLFormElement
          )
        ) {
          return;
        }

        const discountForm =
          form.closest(
            '[data-sl-discount-form]'
          );

        if (
          discountForm &&
          controller.drawer.contains(
            discountForm
          )
        ) {
          event.preventDefault();
          event.stopImmediatePropagation();

          void controller.applyDiscount(
            discountForm
          );

          return;
        }

        if (
          !controller.isCartAddForm(
            form
          )
        ) {
          return;
        }

        if (
          event.submitter?.matches?.(
            '[data-no-cart-drawer], [data-cart-redirect]'
          )
        ) {
          return;
        }

        event.preventDefault();
        event.stopImmediatePropagation();

        void controller.addFromForm(
          form,
          event.submitter
        );
      },
      true
    );

    EXTERNAL_CART_EVENTS.forEach(
      (eventName) => {
        document.addEventListener(
          eventName,
          (event) => {
            if (
              event?.detail?.source ===
              'scentlabs-cart-drawer'
            ) {
              return;
            }

            const controller =
              getController();

            if (!controller) return;

            const shouldOpen =
              eventName ===
                'cart:open' ||
              eventName ===
                'drawer:open' ||
              Boolean(
                event?.detail?.openCart ||
                  event?.detail
                    ?.openDrawer
              );

            if (shouldOpen) {
              controller.open(
                event?.detail?.trigger
              );
            }

            controller.scheduleRefresh({
              openAfterRender:
                shouldOpen
            });
          }
        );
      }
    );

    document.addEventListener(
      'shopify:section:load',
      (event) => {
        const drawer =
          event.target?.querySelector?.(
            DRAWER_SELECTOR
          ) ||
          document.querySelector(
            DRAWER_SELECTOR
          );

        enhanceDrawer(drawer);
      }
    );
  };

  let initialized = false;

  const initializeOnce = () => {
    if (initialized) return;

    initialized = true;

    /*
      FAILSAFE:
      Carten må aldrig starte aktiv ved normal sideindlæsning.
    */

    document
      .querySelectorAll(
        DRAWER_SELECTOR
      )
      .forEach((drawer) => {
        drawer.classList.remove(
          'active',
          'animate'
        );

        drawer.setAttribute(
          'aria-hidden',
          'true'
        );
      });

    document.body.classList.remove(
      'overflow-hidden'
    );

    initialize();
  };

  initializeOnce();

  document.addEventListener(
    'DOMContentLoaded',
    () => {
      getController();
    },
    {
      once: true
    }
  );

  window.ScentLabsCartDrawer = {
    open(triggeredBy) {
      const controller =
        getController();

      controller?.open(
        triggeredBy
      );

      controller?.scheduleRefresh({
        openAfterRender: false
      });
    },

    refresh(options = {}) {
      return (
        getController()?.refresh(
          options
        ) ||
        Promise.resolve(false)
      );
    },

    get element() {
      return document.querySelector(
        DRAWER_SELECTOR
      );
    }
  };
})();

/* V67: søgning i kurvens duftliste (delegeret, overlever gen-rendering af kurven) */
(function () {
  if (window.__slCartSearchReady) return;
  window.__slCartSearchReady = true;
  document.addEventListener('input', function (e) {
    var input = e.target && e.target.closest && e.target.closest('[data-sl-cart-search]');
    if (!input) return;
    var section = input.closest('.sl-cart-upsell');
    if (!section) return;
    var q = input.value.trim().toLowerCase();
    var shown = 0;
    section.querySelectorAll('[data-sl-search-text]').forEach(function (card) {
      var hit = !q || card.getAttribute('data-sl-search-text').indexOf(q) !== -1;
      card.hidden = !hit;
      if (hit) shown++;
    });
    var empty = section.querySelector('[data-sl-cart-empty]');
    if (empty) empty.hidden = shown > 0;
  });
})();


/* V68: vis alle dufte ved søgning eller tryk på knappen */
(function () {
  if (window.__slCartMoreReady) return;
  window.__slCartMoreReady = true;
  function expand(section) {
    var list = section && section.querySelector('[data-sl-cart-list]');
    if (list) list.classList.add('is-expanded');
  }
  document.addEventListener('click', function (e) {
    var btn = e.target && e.target.closest && e.target.closest('[data-sl-cart-more]');
    if (!btn) return;
    expand(btn.closest('.sl-cart-upsell'));
  });
  document.addEventListener('focusin', function (e) {
    var input = e.target && e.target.closest && e.target.closest('[data-sl-cart-search]');
    if (input) expand(input.closest('.sl-cart-upsell'));
  });
  document.addEventListener('input', function (e) {
    var input = e.target && e.target.closest && e.target.closest('[data-sl-cart-search]');
    if (input) expand(input.closest('.sl-cart-upsell'));
  });
})();


/* V69: skriveeffekt i søgefeltets placeholder: "Søg efter Sauvage | Black Opium | ..." */
(function () {
  if (window.__slTypeReady) return;
  window.__slTypeReady = true;
  var reduce = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var state = new WeakMap();
  var fallback = ['Sauvage', 'Black Opium', 'Paradigme', 'Baccarat Rouge'];
  function words(input) {
    var sec = input.closest('.sl-cart-upsell');
    var out = [];
    if (sec) sec.querySelectorAll('[data-sl-name]').forEach(function (n) {
      var v = (n.getAttribute('data-sl-name') || '').trim();
      if (v && out.indexOf(v) < 0) out.push(v);
    });
    return out.length ? out : fallback;
  }
  setInterval(function () {
    if (document.hidden) return;
    document.querySelectorAll('[data-sl-cart-search]').forEach(function (input) {
      if (input.value || document.activeElement === input) { input.placeholder = 'Søg efter parfume'; return; }
      if (reduce) { input.placeholder = 'Søg efter parfume'; return; }
      var s = state.get(input);
      if (!s) { s = { i: 0, n: 0, dir: 1, wait: 6, tick: 0 }; state.set(input, s); }
      s.tick++;
      if (s.wait > 0) { s.wait--; }
      else {
        var list = words(input);
        var word = list[s.i % list.length];
        if (s.dir === 1) {
          s.n++;
          if (s.n >= word.length) { s.n = word.length; s.dir = -1; s.wait = 9; }
        } else {
          s.n--;
          if (s.n <= 0) { s.n = 0; s.dir = 1; s.i++; s.wait = 2; }
        }
      }
      var list2 = words(input);
      var w = list2[s.i % list2.length] || '';
      var cursor = (Math.floor(s.tick / 6) % 2 === 0) ? '|' : '\u00A0';
      input.placeholder = 'Søg efter ' + w.slice(0, s.n) + cursor;
    });
  }, 55);
})();


/* V72: gave-pop-up (gratis valgfri parfume ved 3 dufte) */
(function () {
  if (window.__slGiftPopReady) return;
  window.__slGiftPopReady = true;
  var KEY = 'sl_gift_seen';
  function pop() { return document.querySelector('[data-sl-gift-pop]'); }
  function drawerOpen() {
    var d = document.querySelector('cart-drawer.sl-cart-drawer');
    return !!(d && d.classList.contains('active'));
  }
  function openPop() { var p = pop(); if (p) p.hidden = false; }
  function closePop() { var p = pop(); if (p) p.hidden = true; }
  function sync() {
    var st = document.querySelector('[data-sl-gift-state]');
    if (!st) return;
    var state = st.getAttribute('data-sl-gift-state');
    try {
      if (state !== 'ready') { sessionStorage.removeItem(KEY); return; }
      if (sessionStorage.getItem(KEY) !== '1' && drawerOpen() && pop()) {
        sessionStorage.setItem(KEY, '1');
        openPop();
      }
    } catch (e) {
      if (state === 'ready' && drawerOpen() && pop() && !window.__slGiftShownOnce) { window.__slGiftShownOnce = true; openPop(); }
    }
  }
  document.addEventListener('click', function (e) {
    var t = e.target;
    if (!t || !t.closest) return;
    if (t.closest('[data-sl-gift-open]')) { openPop(); return; }
    if (t.closest('[data-sl-gift-close]')) { closePop(); }
  });
  document.addEventListener('keydown', function (e) { if (e.key === 'Escape') closePop(); });
  var timer = null;
  new MutationObserver(function () {
    clearTimeout(timer);
    timer = setTimeout(sync, 250);
  }).observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
  document.addEventListener('DOMContentLoaded', sync);
})();


/* V79: fejring når fri fragt opnås */
(function () {
  if (window.__slShipCelebrate) return;
  window.__slShipCelebrate = true;
  var KEY = 'sl_ship_seen', timer = null;
  function sync() {
    var row = document.querySelector('.sl-cart-summary__row--shipping');
    try {
      if (!row) { sessionStorage.removeItem(KEY); return; }
      if (sessionStorage.getItem(KEY) === '1') return;
      var d = document.querySelector('cart-drawer.sl-cart-drawer');
      if (!d || !d.classList.contains('active')) return;
      sessionStorage.setItem(KEY, '1');
    } catch (e) { if (!row || window.__slShipOnce) return; window.__slShipOnce = true; }
    row.classList.add('is-new');
    if (row.parentNode.querySelector('.sl-ship-toast')) return;
    var t = document.createElement('div');
    t.className = 'sl-ship-toast';
    t.setAttribute('role', 'status');
    t.innerHTML = '<svg viewBox="0 0 24 24" fill="none" aria-hidden="true"><path d="M3.5 7h11v9.5h-11V7Z" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/><path d="M14.5 10H18l2.5 2.5v4h-6V10Z" stroke="currentColor" stroke-width="1.7" stroke-linejoin="round"/><circle cx="7" cy="17.5" r="1.5" stroke="currentColor" stroke-width="1.7"/><circle cx="17.5" cy="17.5" r="1.5" stroke="currentColor" stroke-width="1.7"/></svg><span>Tillykke – du får GRATIS fragt!</span>';
    row.parentNode.insertBefore(t, row);
    setTimeout(function () { t.classList.add('is-out'); setTimeout(function () { t.remove(); }, 500); }, 4000);
  }
  new MutationObserver(function () { clearTimeout(timer); timer = setTimeout(sync, 200); })
    .observe(document.documentElement, { childList: true, subtree: true, attributes: true, attributeFilter: ['class'] });
  document.addEventListener('DOMContentLoaded', sync);
})();
